'use client';

import { useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { Link } from '@/i18n/navigation';
import { WordReveal } from '@/components/motion/WordReveal';
import { Clock, MapPin, Phone } from 'lucide-react';
import { localized, type HomeContentConfig, type RestaurantLocale } from '@restaurant/config';
import type { CurrentOpeningStatus } from '@restaurant/db';
import { HeroLogoShowcase } from './hero-logo/HeroLogoShowcase';
import styles from './HeroSection.module.css';

const EASE = [0.22, 1, 0.36, 1] as const;

interface HeroSectionProps {
  content: HomeContentConfig;
  locale: RestaurantLocale;
  openingStatus?: CurrentOpeningStatus;
}

export function HeroSection({ content, locale, openingStatus }: HeroSectionProps) {
  const reduced = useReducedMotion();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const hero = content.hero;
  const reservationHref = `tel:${content.phone.replace(/\s/g, '')}`;
  const address = `${content.addressLine1}, ${content.addressLine2}`;
  const todayHours = openingStatus
    ? `${openingStatus.label} · ${openingStatus.detail}`
    : localized(content.quickInfo.find((block) => block.key === 'hours')?.line2 ?? content.quickInfo[0].line2, locale);

  /* Server + first hydration: render final state, no animation attributes */
  if (!mounted || reduced) {
    return (
      <section className={styles.hero}>
        <div className={styles.heroGrid}>
          <div className={styles.content}>
            <span className={styles.eyebrow}>{localized(hero.eyebrow, locale)}</span>
            <h1 className={styles.title}>{localized(hero.title, locale)}</h1>
            <p className={styles.subtitle}>{localized(hero.subtitle, locale)}</p>
            <div className={styles.actions}>
              {hero.showMenuCta && <Link href="/menu" className={styles.primaryButton}>{localized(hero.menuCta, locale)}</Link>}
              {hero.showReservationCta && <a href={reservationHref} className={styles.secondaryButton}>{localized(hero.reservationCta, locale)}</a>}
            </div>
          </div>
          <div className={styles.visualPanel}>
            <HeroLogoShowcase locale={locale} alt={localized(hero.imageAlt, locale)} />
          </div>
        </div>
        <div className={styles.infoLine}>
          <div className={styles.infoContainer}>
            <div className={styles.infoItem}>
              <MapPin aria-hidden="true" />
              <span>{address}</span>
            </div>
            <div className={styles.infoItem}>
              <Clock aria-hidden="true" />
              <span>{todayHours}</span>
            </div>
            <div className={styles.infoItem}>
              <Phone aria-hidden="true" />
              <span>{content.phone}</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  /* Client after mount: full entrance animation sequence */
  return (
    <section className={styles.hero}>
      <div className={styles.heroGrid}>
        <div className={styles.content}>
          <motion.span
            className={styles.eyebrow}
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
          >
            {localized(hero.eyebrow, locale)}
          </motion.span>

          <h1 className={styles.title}>
            <WordReveal text={localized(hero.title, locale)} delay={0.25} stagger={0.09} />
          </h1>

          <motion.p
            className={styles.subtitle}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
          >
            {localized(hero.subtitle, locale)}
          </motion.p>

          <motion.div
            className={styles.actions}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.85, ease: EASE }}
          >
            {hero.showMenuCta && <Link href="/menu" className={styles.primaryButton}>{localized(hero.menuCta, locale)}</Link>}
            {hero.showReservationCta && <a href={reservationHref} className={styles.secondaryButton}>{localized(hero.reservationCta, locale)}</a>}
          </motion.div>
        </div>

        <motion.div
          className={styles.visualPanel}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.1, ease: EASE }}
        >
          <HeroLogoShowcase locale={locale} alt={localized(hero.imageAlt, locale)} />
        </motion.div>
      </div>

      <motion.div
        className={styles.infoLine}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.0, ease: EASE }}
      >
        <div className={styles.infoContainer}>
          <div className={styles.infoItem}>
            <MapPin aria-hidden="true" />
            <span>{address}</span>
          </div>
          <div className={styles.infoItem}>
            <Clock aria-hidden="true" />
            <span>{todayHours}</span>
          </div>
          <div className={styles.infoItem}>
            <Phone aria-hidden="true" />
            <span>{content.phone}</span>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
